/*
 * bandeau-annonce.js
 * Defilement des messages du bandeau (sections/bandeau-annonce.liquid) et
 * bouton pause / lecture.
 *
 * Custom element : l'editeur de theme peut ajouter, retirer ou recharger la
 * section sans que rien ne soit a rebrancher (connected/disconnectedCallback).
 *
 * Le defilement s'arrete dans quatre cas : clic sur le bouton, onglet en
 * arriere-plan, focus clavier a l'interieur du bandeau, et reglage systeme
 * "animations reduites" (le bandeau demarre alors en pause).
 */
class BandeauAnnonce extends HTMLElement {
  connectedCallback() {
    this.messages = Array.from(this.querySelectorAll('[data-message]'));
    this.bouton = this.querySelector('[data-pause]');
    this.index = Math.max(
      this.messages.findIndex((message) => message.classList.contains('is-active')),
      0
    );
    this.duree = Number(this.dataset.duree) || 5000;
    this.rotationDemandee = this.dataset.rotation === 'true' && this.messages.length > 1;
    this.minuteur = null;

    this.animationsReduites = window.matchMedia('(prefers-reduced-motion: reduce)');
    // En pause a la demande de l'utilisateur (bouton) ou de son systeme.
    this.enPause = this.animationsReduites.matches;

    this.onClicBouton = this.basculer.bind(this);
    this.onVisibilite = this.appliquer.bind(this);
    this.onFocusIn = this.appliquer.bind(this);
    this.onFocusOut = this.appliquer.bind(this);

    if (this.bouton) {
      this.bouton.addEventListener('click', this.onClicBouton);
      this.majBouton();
    }
    document.addEventListener('visibilitychange', this.onVisibilite);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);

    this.appliquer();
  }

  disconnectedCallback() {
    this.arreter();
    if (this.bouton) this.bouton.removeEventListener('click', this.onClicBouton);
    document.removeEventListener('visibilitychange', this.onVisibilite);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
  }

  /* Le defilement ne tourne que si tout le monde est d'accord. */
  doitTourner() {
    if (!this.rotationDemandee || this.enPause) return false;
    if (document.hidden) return false;
    return !this.contains(document.activeElement) || document.activeElement === this.bouton;
  }

  appliquer() {
    if (this.doitTourner()) {
      this.demarrer();
    } else {
      this.arreter();
    }
  }

  demarrer() {
    if (this.minuteur) return;
    this.minuteur = setInterval(() => this.suivant(), this.duree);
  }

  arreter() {
    if (!this.minuteur) return;
    clearInterval(this.minuteur);
    this.minuteur = null;
  }

  basculer() {
    this.enPause = !this.enPause;
    this.majBouton();
    this.appliquer();
  }

  majBouton() {
    if (!this.bouton) return;
    const libelle = this.enPause
      ? this.bouton.dataset.libelleLecture || this.bouton.getAttribute('aria-label')
      : this.bouton.dataset.libellePause || this.bouton.getAttribute('aria-label');
    this.bouton.setAttribute('aria-pressed', String(this.enPause));
    this.bouton.setAttribute('aria-label', libelle);
    this.bouton.setAttribute('title', libelle);
  }

  suivant() {
    const precedent = this.messages[this.index];
    this.index = (this.index + 1) % this.messages.length;
    const actuel = this.messages[this.index];

    if (precedent) {
      precedent.classList.remove('is-active');
      precedent.setAttribute('aria-hidden', 'true');
    }
    actuel.classList.add('is-active');
    actuel.removeAttribute('aria-hidden');
  }
}

if (!customElements.get('bandeau-annonce')) {
  customElements.define('bandeau-annonce', BandeauAnnonce);
}
